import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ClinicalFeedbackConstants } from '@app/clinical-feedback/clinical-feedback-constants';
import { FormElementModel } from '@app/clinical-feedback/clinical-feedback.model';
import { NewClinicalFeedbackFormHelper, ShowLogic } from '@app/clinical-feedback/new-clinical-feedback/new-clinical-feedback.helper';
import { combineLatest, startWith, Subscription } from 'rxjs';

@Component({
    selector: 'app-capture-details',
    templateUrl: './capture-details.component.html'
})
export class CaptureDetailsComponent implements OnInit, OnDestroy {
    @Input() parentFormGroup!: UntypedFormGroup;
    @Input() showSeriousIncident = true;
    formElementModels = {
        wasDiscrepancyRaised: new FormElementModel({
            id: 'wasDiscrepancyRaised',
            label: 'Was a discrepancy raised internally at the hospital?',
            fieldName: 'WasDiscrepancyRaised',
            errorMessages: {
                required: ClinicalFeedbackConstants.ERROR_MESSAGE_REQUIRED
            }
        }),
        wasSeriousIncidentRaised: new FormElementModel({
            id: 'wasSeriousIncidentRaised',
            label: 'Was a Serious Incident raised previously?',
            fieldName: 'WasSeriousIncidentRaised',
            errorMessages: {
                required: ClinicalFeedbackConstants.ERROR_MESSAGE_REQUIRED
            }
        }),
        wasEverlightInformedOfDiscrepancy: new FormElementModel({
            id: 'wasEverlightInformedOfDiscrepancy',
            label: 'Has Everlight been informed of the discrepancy previously?',
            fieldName: 'WasEverlightInformedOfDiscrepancy',
            errorMessages: {
                required: ClinicalFeedbackConstants.ERROR_MESSAGE_REQUIRED
            }
        }),
        wasEverlightInformedOfSeriousIncident: new FormElementModel({
            id: 'wasEverlightInformedOfSeriousIncident',
            label: 'Has Everlight been informed of the Serious incident previously?',
            fieldName: 'WasEverlightInformedOfSeriousIncident',
            errorMessages: {
                required: ClinicalFeedbackConstants.ERROR_MESSAGE_REQUIRED
            }
        }),
        detailsOfDiscrepancy: new FormElementModel({
            id: 'detailsOfDiscrepancy',
            errorMessages: { required: 'Details are required' },
            label: 'Add details here',
            fieldName: 'detailsOfDiscrepancy',
            placeholder: 'Add text'
        })
    };

    public showWasEverlightInformedOfDiscrepancy = false;
    public showWasEverlightInformedOfSeriousIncident = false;
    public showDetailsOfDiscrepancy = false;

    private subscriptions!: Subscription;

    ngOnInit(): void {
        this.subscriptions = new Subscription();
        this.parentFormGroup.addControl('wasDiscrepancyRaised', new UntypedFormControl(null, [Validators.required]));
        this.parentFormGroup.addControl(
            'wasEverlightInformedOfDiscrepancy',
            new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
        );
        if (this.showSeriousIncident) {
            this.parentFormGroup.addControl('wasSeriousIncidentRaised', new UntypedFormControl(null, [Validators.required]));
            this.parentFormGroup.addControl(
                'wasEverlightInformedOfSeriousIncident',
                new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
            );
        }
        this.parentFormGroup.addControl(
            'detailsOfDiscrepancy',
            new UntypedFormControl({ value: '', disabled: true }, [Validators.required])
        );

        this.handleFormValueChanges();
    }

    handleFormValueChanges() {
        this.subscriptions.add(
            NewClinicalFeedbackFormHelper.SetVisibilityOfControlsOnBooleanEvent(
                this.controls['wasDiscrepancyRaised'].valueChanges,
                [this.controls['wasEverlightInformedOfDiscrepancy']],
                (visibility: boolean) => {
                    this.showWasEverlightInformedOfDiscrepancy = visibility;
                },
                ShowLogic.ShowOnYes
            )
        );

        if (this.showSeriousIncident) {
            this.subscriptions.add(
                NewClinicalFeedbackFormHelper.SetVisibilityOfControlsOnBooleanEvent(
                    this.controls['wasSeriousIncidentRaised'].valueChanges,
                    [this.controls['wasEverlightInformedOfSeriousIncident']],
                    (visibility: boolean) => {
                        this.showWasEverlightInformedOfSeriousIncident = visibility;
                    },
                    ShowLogic.ShowOnYes
                )
            );

            this.subscriptions.add(
                combineLatest([
                    this.controls['wasEverlightInformedOfDiscrepancy'].valueChanges.pipe(startWith(null)),
                    this.controls['wasEverlightInformedOfSeriousIncident'].valueChanges.pipe(startWith(null))
                ]).subscribe(([wasInformedOfDiscrepancy, wasInformedOfSeriousIncident]) => {
                    if (wasInformedOfDiscrepancy === false || wasInformedOfSeriousIncident === false) {
                        this.showDetailsOfDiscrepancy = true;
                        this.controls['detailsOfDiscrepancy'].enable();
                    } else {
                        this.showDetailsOfDiscrepancy = false;
                        this.controls['detailsOfDiscrepancy'].disable();
                        this.controls['detailsOfDiscrepancy'].reset();
                    }
                })
            );
        } else {
            this.subscriptions.add(
                NewClinicalFeedbackFormHelper.SetVisibilityOfControlsOnBooleanEvent(
                    this.controls['wasEverlightInformedOfDiscrepancy'].valueChanges,
                    [this.controls['detailsOfDiscrepancy']],
                    (visibility: boolean) => {
                        this.showDetailsOfDiscrepancy = visibility;
                    },
                    ShowLogic.ShowOnNo
                )
            );
        }
    }

    get controls() {
        return this.parentFormGroup.controls;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
